import type { TsTaxRateComponentApplicationMode } from "../u3110TaxRate/U3110Service";
import type { U3130PeriodPreset } from "./tools/U3130PeriodPresets";
import type { U3130ApprovalListPreset } from "./tools/u3130ApprovalListPreset";

export type { U3130PeriodPreset };
export type { U3130ApprovalListPreset };


/** Statut feuille de temps (aligné backend TsTimesheetStatus). */
export type U3130TimesheetStatus = "Open" | "WaitingForApproval" | "Approve" | "WaitingForGenerateTEC" | "Close";

/** Rémunération employé dans le bloc `employee` du GET feuille (aligné backend, même schéma que U3129). */
export type U3130RemunerationType = "Hourly" | "Salary";

/** Référentiel type équipement (initLU) — aligné GET /U3130/initLU (schéma backend U3130EquipmentTypeItem). */
export interface U3130EquipmentTypeItem {
  id: string;
  code?: string;
  description?: string;
  billingUnit?: string;
  amount: number;
  active: number;
  majorFactor: number;
}

/** Ligne composante dans `taxRates[]` de initLU (directive U3110 §13, schéma U3130). */
export interface U3130TaxRateComponentRef {
  sortOrder: number;
  rate: number;
  labelFr?: string | null;
  labelEn?: string | null;
  applicationMode: TsTaxRateComponentApplicationMode | null;
}

/** Référentiel taux de taxe (initLU). */
export interface U3130TaxRateItem {
  id: string;
  code?: string;
  /** Taux agrégé (%) ; peut être absent selon la payload initLU — utiliser {@link u3130TaxRateDisplayPercent}. */
  rate?: number;
  description?: string;
  /** Si `false`, régime inactif — exclure des combos (défaut true si absent). */
  active?: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  /** Optionnel : détail des composantes pour calculs / affichage avancé. */
  taxRateComponents?: U3130TaxRateComponentRef[];
}

/** Taux sélectionnable en approbation : actif uniquement (pas de filtre géographique). */
export function u3130IsTaxRateActive(item: U3130TaxRateItem): boolean {
  return item.active !== false;
}

/** Pourcentage affichable pour un taux (champ `rate` ou somme des composantes). */
export function u3130TaxRateDisplayPercent(item: U3130TaxRateItem): number | null {
  if (typeof item.rate === "number" && Number.isFinite(item.rate)) {
    return item.rate;
  }
  const comps = item.taxRateComponents;
  if (comps?.length) {
    let sum = 0;
    let hasAny = false;
    for (const c of comps) {
      const r = c.rate;
      if (typeof r === "number" && Number.isFinite(r)) {
        sum += r;
        hasAny = true;
      }
    }
    if (hasAny) {
      return sum;
    }
  }
  return null;
}

/** Libellé combo « Taux taxe » : évite « undefined% » si `rate` est absent ; option vide = description seule. */
export function u3130FormatTaxRateComboLabel(item: U3130TaxRateItem): string {
  if (!item.id?.trim()) {
    return item.description?.trim() ?? "";
  }
  const pct = u3130TaxRateDisplayPercent(item);
  const code = item.code?.trim();
  const ratePart = pct != null ? `${pct}%` : "";

  if (code) {
    return ratePart ? `${code} (${ratePart})` : code;
  }
  if (ratePart) {
    return ratePart;
  }
  return item.description?.trim() ?? "";
}

/** Référentiel type dépense (initLU) — GET /U3130/initLU result.expenseTypes[]. */
export interface U3130ExpenseTypeItem {
  id: string;
  code?: string | null;
  description?: string | null;
  /** Souvent utilisé comme coût unitaire côté API si `unitCost` est absent. */
  factor?: number | null;
  /** Défaut métier 0 après migration. */
  majorFactor?: number;
  /** Petit entier (ex. actif/inactif) ; défaut métier 1. */
  status?: number;
  /** Coût unitaire (initLU / API), alimente la ligne dépense à la sélection du type. */
  unitCost?: number | null;
}

/** Tâche (initLU). */
export interface U3130TaskItem {
  id: string;
  taskCode?: string;
  /** Libellé tâche (initLU), selon langue UI. */
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
}

/** Personnalisations retournées par initLU (comme U3121). */
export interface U3130InitLUPersonalization {
  admIncludeSubProject: string | null;
  /** BL-0606 : org legacy — pas de garde-fou soldes banques à la soumission. */
  skipHourBankBalanceValidationOnSubmit?: boolean;
  /** BL-0675 : masquer onglets Dépenses / Équipement (même flag org que U3129). */
  hideExpenseAndEquipmentTabs?: boolean;
}

/** Employé associé à l'usager courant (initLU). Null si l'usager n'est pas configuré pour faire sa feuille de temps. */
export interface U3130InitLUEmployee {
  id: string;
  firstName: string;
  lastName: string;
  vacationBncHours?: number;
  vacationNegativeThresholdHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
}

/** Période paie active renvoyée par GET U3130/initLU (`activePayrollPeriod`). */
export interface U3130ActivePayrollPeriodItem {
  /** Année paie / exercice (ex. année fiscale affichée avec la période). */
  yearCode?: string;
  periodNumber: number;
  startAt: string;
  endAt: string;
}

/** Option initLU : entrée combo « période paie » pour le rapport sommaire paie (si le backend l’expose). */
export interface U3130SommairePaiePayrollPeriodOption {
  id: string;
  label: string;
  startAt: string;
  endAt: string;
  /** Renseigné quand l’option provient de `activePayrollPeriod` (export PDF). */
  yearCode?: string;
  periodNumber?: number;
}

export interface U3130InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  /** Préréglage filtre période (liste) ; affiché en puces dans BasicSearch. Défaut frontend : semaine courante. */
  defaultPeriodPreset?: U3130PeriodPreset;
  /** Préréglage liste approbation : défaut frontend `waiting_for_approval`. */
  defaultApprovalListPreset?: U3130ApprovalListPreset;
  equipmentTypes: U3130EquipmentTypeItem[];
  taxRates: U3130TaxRateItem[];
  expenseTypes: U3130ExpenseTypeItem[];
  /** Optionnel : les tâches honoraires par ligne viennent de `findTasksForProject` ; ne plus alimenter pour alléger initLU. */
  tasks?: U3130TaskItem[];
  /** Personnalisations d'organisation (ex. ADM_INCLUDE_SUB_PROJET). */
  personalization?: U3130InitLUPersonalization;
  /** Employé lié à l'usager. Null = usager non configuré, afficher la page « Contacter votre administrateur ». */
  employee?: U3130InitLUEmployee | null;
  /** Périodes paie actives pour le rapport sommaire paie (GET initLU). */
  activePayrollPeriod?: U3130ActivePayrollPeriodItem[];
  /** Optionnel : périodes préformatées (alternative à `activePayrollPeriod`). */
  sommairePaiePayrollPeriods?: U3130SommairePaiePayrollPeriodOption[];
  /** Fuseau d'affichage organisation (IANA). Défaut frontend : America/Montreal. */
  organizationTimezone?: string;
}

/** Convention UI : type de dépense sélectionnable si status === 1 (défaut métier après migration). */
export function u3130IsExpenseTypeActive(item: U3130ExpenseTypeItem): boolean {
  return (item.status ?? 1) === 1;
}

/** majorFactor normalisé (initLU / affichage). */
export function u3130ExpenseTypeMajorFactor(item: U3130ExpenseTypeItem): number {
  return item.majorFactor ?? 0;
}

/** Libellé type dépense (description, code ou id) — sans coût ni coefficient (colonne « Coût unitaire »). */
export function u3130ExpenseTypeLabel(item: U3130ExpenseTypeItem): string {
  return (item.description?.trim() || item.code?.trim() || item.id) ?? item.id;
}

/** Alias historique : même rendu que {@link u3130ExpenseTypeLabel} (plus de valeur entre parenthèses dans le combo). */
export function u3130ExpenseTypeLabelWithFactor(item: U3130ExpenseTypeItem): string {
  return u3130ExpenseTypeLabel(item);
}

/**
 * Coût unitaire depuis le référentiel type dépense : `unitCost` si défini, sinon repli sur `factor` (API existante).
 */
export function u3130ExpenseTypeUnitCost(item: U3130ExpenseTypeItem): number {
  const u = item.unitCost;
  if (typeof u === "number" && Number.isFinite(u)) return u;
  const f = item.factor;
  if (typeof f === "number" && Number.isFinite(f)) return f;
  return 0;
}

/** Ligne avec coût unitaire > 0 : montant dérivé (coût × quantité entière). */
export function u3130ExpenseLineUsesUnitCost(row: Pick<U3130ExpenseItem, "unitCost">): boolean {
  const u = row.unitCost;
  return typeof u === "number" && Number.isFinite(u) && u > 0;
}

/** Montant = coût unitaire × quantité entière (arrondi 2 décimales). */
export function u3130ComputeExpenseLineAmountFromUnit(row: Pick<U3130ExpenseItem, "unitCost" | "quantity">): number {
  const c = typeof row.unitCost === "number" && Number.isFinite(row.unitCost) ? row.unitCost : 0;
  const q = row.quantity;
  const qi = typeof q === "number" && Number.isFinite(q) && Number.isInteger(q) ? q : 0;
  return Math.round(c * qi * 100) / 100;
}

/** Type équipement sélectionnable si actif (active === 1, défaut métier). */
export function u3130IsEquipmentTypeActive(item: U3130EquipmentTypeItem): boolean {
  return (item.active ?? 1) === 1;
}

/** Montant unitaire normalisé (initLU). */
export function u3130EquipmentTypeAmount(item: U3130EquipmentTypeItem): number {
  return typeof item.amount === "number" && Number.isFinite(item.amount) ? item.amount : 0;
}

/** Facteur de majoration normalisé (initLU). */
export function u3130EquipmentTypeMajorFactor(item: U3130EquipmentTypeItem): number {
  return item.majorFactor ?? 0;
}

/** Projet (findProject). */
export interface U3130FindProjectItem {
  id: string;
  projetNumber?: string;
  projetName?: string;
  /** Si true, le champ sous-projet est disponible pour cette ligne. */
  includeSubProjet?: boolean;
  customerCode?: string;
  customerName?: string;
}

/** Sous-projet (findSubproject). */
export interface U3130FindSubprojectItem {
  id: string;
  projectId: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

/** Employé (autocomplete findEmployee). */
export interface U3130FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
}

/** Projet (objet retourné par le GET sur une ligne honoraire). */
export interface U3130HonorariumProjectRef {
  id: string;
  projetNumber?: string;
  /** Nom / description U3121 (lecture GET — BL-0679). */
  projetName?: string;
  nomClient?: string;
  /** Libellé bloc client calculé backend (BL-0679). */
  clientBlockLabel?: string;
  /** Codes natures du projet, ordre MyEvent (ex. `CAD+CL+IMP`) — BL-0677. */
  workNatureDisplay?: string;
}

/** Tâche (objet retourné par le GET sur une ligne honoraire). */
export interface U3130HonorariumTaskRef {
  id: string;
  description?: string;
  /** Codes natures liées à la tâche et au projet — BL-0677. */
  workNatureDisplay?: string;
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
}

/** Identifiant stable côté UI pour associer cache de tâches (projet/sous-projet) à une ligne honoraire. */
export function newHonorariumClientRowId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `hr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Ligne honoraire. */
export interface U3130HonorariumItem {
  id?: string;
  timesheetId?: string;
  /** Clé client pour le cache des listes de tâches par ligne (non envoyée au backend). */
  clientRowId?: string;
  projectId: string;
  subprojectId?: string;
  monday?: number;
  tuesday?: number;
  wednesday?: number;
  thursday?: number;
  friday?: number;
  saturday?: number;
  sunday?: number;
  taskType?: string;
  version?: number;
  /** Objet projet (réponse GET). */
  project?: U3130HonorariumProjectRef;
  /** Objet sous-projet (réponse GET). */
  subproject?: U3130LineSubprojectRef | U3130FindSubprojectItem;
  /** Objet tâche (réponse GET). */
  task?: U3130HonorariumTaskRef;
  totalHours?: number;
}

/** Sous-projet (objet retourné par le GET sur une ligne dépense / équipement). */
export interface U3130LineSubprojectRef {
  id: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

/** Ligne dépense. */
export interface U3130ExpenseItem {
  id?: string;
  timesheetId?: string;
  transactionDate?: string | null;
  projectId: string;
  subprojectId?: string;
  expenseTypeId: string;
  /** Coût unitaire (souvent issu du type de dépense ; lecture seule en UI). `null` = rien à afficher. */
  unitCost?: number | null;
  taxRateId?: string;
  quantity?: number | null;
  amount: number;
  remark?: string;
  version?: number;
  /** Clé stable pour les listes éditables (non envoyée au backend). */
  clientRowId?: string;
  /** Objet projet (réponse GET). */
  project?: U3130HonorariumProjectRef | U3130FindProjectItem;
  /** Objet sous-projet (réponse GET). */
  subproject?: U3130LineSubprojectRef | U3130FindSubprojectItem;
  /** Affichage client (UI, non API). */
  clientDisplay?: string;
  checked?: boolean;
}

/** Ligne équipement. */
export interface U3130EquipmentItem {
  id?: string;
  timesheetId?: string;
  transactionDate?: string | null;
  projectId: string;
  subprojectId?: string;
  equipmentTypeId: string;
  billingUnit?: string;
  equipmentCost?: number;
  quantity?: number;
  amount?: number;
  remark?: string;
  version?: number;
  clientRowId?: string;
  project?: U3130HonorariumProjectRef | U3130FindProjectItem;
  subproject?: U3130LineSubprojectRef | U3130FindSubprojectItem;
  clientDisplay?: string;
  checked?: boolean;
}

/** Ligne banque d'heures. */
export interface U3130BankHoursItem {
  id?: string;
  timesheetId?: string;
  bankId?: string;
  monday?: number;
  tuesday?: number;
  wednesday?: number;
  thursday?: number;
  friday?: number;
  saturday?: number;
  sunday?: number;
  version?: number;
  /** Total heures sur la ligne (calcul UI, non envoyé au backend). */
  totalHours?: number;
}

/**
 * Ordre et identifiants `bankId` : vacances, férié, maladie, congé autorisé (`authorizedLeave`), banque spéciale (`special`).
 * `authorizedLeave` : absence sans solde BNC ni plafond de saisie côté UI.
 */
export const U3130_BANK_HOUR_TYPE_ORDER = [
  "vacation",
  "holidayLeave",
  "sickLeave",
  "authorizedLeave",
  "special",
  "specialDeposit",
] as const;

export type U3130BankHourTypeId = (typeof U3130_BANK_HOUR_TYPE_ORDER)[number];

export function emptyBankHoursRow(bankId: U3130BankHourTypeId): U3130BankHoursItem {
  return {
    bankId,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };
}

function normalizeBankHoursBankId(raw: string | undefined): string | null {
  const id = raw?.trim();
  return id || null;
}

/** Fusionne avec le référentiel des types pour toujours avoir une ligne par `bankId`, dans l’ordre métier. */
export function ensureBankHoursList(list: U3130BankHoursItem[] | undefined | null): U3130BankHoursItem[] {
  const byId = new Map<string, U3130BankHoursItem>();
  for (const item of list ?? []) {
    const id = normalizeBankHoursBankId(item.bankId);
    if (id) {
      byId.set(id, { ...item, bankId: id });
    }
  }
  return U3130_BANK_HOUR_TYPE_ORDER.map((bankId) => {
    const existing = byId.get(bankId);
    if (existing) {
      return { ...emptyBankHoursRow(bankId), ...existing, bankId };
    }
    return emptyBankHoursRow(bankId);
  });
}

/** Employé (résumé dans timesheet) — GET `/:id` `result.employee` ; champs additionnels tolérés pour rétrocompat API. */
export interface U3130EmployeeRef {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
  /** GET détail : horaire vs salarié. */
  remunerationType?: U3130RemunerationType;
  /** Base horaire (GET, lecture seule) — peut être absent si seul `remunerationType` est fourni. */
  hourlyBase?: number | null;
  vacationBncHours?: number;
  vacationNegativeThresholdHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
}

/**
 * Feuille (approbation) — corps `result` du GET `U3130/:id`, aligné sur le détail U3129 côté backend.
 * Les totaux search peuvent être absents sur le GET selon le backend.
 */
export interface U3130Timesheet {
  id: string;
  organizationId?: string;
  employeeId: string;
  periodDate: string;
  status: U3130TimesheetStatus;
  /** Total hebdomadaire retourné par le GET backend. */
  totalHours?: number;
  /** Total heures travaillées (honoraires / projets uniquement, sans banques d’heures) — recalcul UI. */
  totalPaidHours?: number;
  /** Total absences (4 congés) — calcul UI. */
  totalLeaveHours?: number;
  totalSpecialWithdraw?: number;
  totalSpecialDeposit?: number;
  totalSpecialNet?: number;
  /**
   * Heures « payées » telles qu’à l’approbation (snapshot GET).
   * Le détail recalcule les totaux à partir des lignes banque / honoraires.
   */
  approvalPaidHours?: number;
  /** Total monétaire des dépenses (search backend). */
  totalExpenses?: number;
  /** Total monétaire des équipements (search backend). */
  totalEquipment?: number;
  vacationBalanceBeforeTimesheet?: number;
  vacationDebitHours?: number;
  vacationBalanceAfterTimesheet?: number;
  vacationThresholdHours?: number;
  vacationThresholdExceededHours?: number;
  vacationBankWillBeNegative?: boolean;
  /** Nouveaux noms retournés par le GET backend. */
  vacationBncHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
  unpaidHours?: number;
  regularHoursToBank?: number;
  halfTimeHoursToBank?: number;
  doubleTimeHoursToBank?: number;
  version: number;
  honoraria?: U3130HonorariumItem[];
  expenses?: U3130ExpenseItem[];
  equipment?: U3130EquipmentItem[];
  bankHoursList?: U3130BankHoursItem[];
  employee?: U3130EmployeeRef;
  /** Libellé affiché (prénom + nom), non envoyé au backend. */
  employeeDisplay?: string;
  /** Libellé affiché pour la période (lundi – dimanche), non envoyé au backend. */
  periodDisplay?: string;
  /** Libellé traduit du statut (UI, non envoyé au backend). */
  statusDisplay?: string;
  /** Totaux footer onglet honoraires — heures projet uniquement (UI, non envoyé au backend). */
  honorariaFooterTotals?: Record<string, number>;
  /** Totaux footer — absences / banques (UI). */
  bankHoursFooterTotals?: Record<string, number>;
  /** Totaux combinés projet + absences (UI). */
  combinedWeekFooterTotals?: Record<string, number>;
}

export const emptyTimesheet: U3130Timesheet = {
  id: "",
  employeeId: "",
  periodDate: "",
  status: "Open",
  totalHours: 0,
  totalPaidHours: 0,
  totalLeaveHours: 0,
  approvalPaidHours: 0,
  totalExpenses: 0,
  totalEquipment: 0,
  vacationBncHours: 0,
  holidayBncHours: 0,
  sickLeaveBncHours: 0,
  specialBncHours: 0,
  unpaidHours: 0,
  regularHoursToBank: 0,
  halfTimeHoursToBank: 0,
  doubleTimeHoursToBank: 0,
  version: 0,
  honoraria: [],
  expenses: [],
  equipment: [],
  bankHoursList: [],
};

/** Critères autocomplete (findProject / findSubproject). */
export type U3130AutoCompletCriterias = { fieldName: string; value: unknown }[];

/** Corps POST findAvailablePeriods : périodes candidates (lundis ISO) + employé cible. */
export interface U3130FindAvailablePeriodsRequest {
  employeeId: string;
  /** Liste des lundis ISO (YYYY-MM-DD) proposés côté UI ; le serveur renvoie le sous-ensemble autorisé. */
  periods: string[];
}

/** Corps POST findTasksForProject : tâches autorisées pour la combinaison projet / sous-projet. */
export interface U3130FindTasksForProjectRequest {
  projectId: string;
  /** Requis si le projet impose un sous-projet ; sinon omis ou vide selon règle backend. */
  subprojectId?: string | null;
}

function normalizeHourlyBaseFromApi(raw: unknown): number | null {
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function firstPresentNumericCandidate(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === undefined || v === null || v === "") continue;
    return v;
  }
  return undefined;
}

function firstPresentIntegerLikeCandidate(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "number" && Number.isInteger(v)) {
      return v;
    }
    if (typeof v === "string") {
      const t = v.trim();
      if (t !== "" && /^\d+$/.test(t)) {
        return parseInt(t, 10);
      }
    }
  }
  return undefined;
}

function pickHourlyBaseRawFromEmployee(emp: U3130EmployeeRef): unknown {
  const rec = emp as unknown as Record<string, unknown>;
  return firstPresentNumericCandidate(rec.hourlyBase, rec.hourly_base, rec.HourlyBase);
}

function pickHourlyBaseRawFromRowRoot(row: U3130Timesheet & { hourlyBase?: unknown }): unknown {
  const rec = row as unknown as Record<string, unknown>;
  return firstPresentNumericCandidate(row.hourlyBase, rec.hourly_base, rec.HourlyBase);
}

function firstPresentStringCandidate(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return undefined;
}

function pickRemunerationTypeFromEmployee(emp: U3130EmployeeRef): U3130RemunerationType | undefined {
  const rec = emp as unknown as Record<string, unknown>;
  const raw = firstPresentStringCandidate(rec.remunerationType, rec.remuneration_type, rec.RemunerationType);
  if (raw === "Hourly" || raw === "Salary") return raw;
  return emp.remunerationType;
}

const U3130_VALID_TIMESHEET_STATUSES = [
  "Open",
  "WaitingForApproval",
  "Approve",
  "WaitingForGenerateTEC",
  "Close",
] as const satisfies readonly U3130TimesheetStatus[];

function isU3130TimesheetStatusString(s: string): s is U3130TimesheetStatus {
  return (U3130_VALID_TIMESHEET_STATUSES as readonly string[]).includes(s);
}

function pickEmployeeObjectFromTimesheetRow(rec: Record<string, unknown>): U3130EmployeeRef | undefined {
  const emp = rec.employee ?? rec.Employee;
  if (emp != null && typeof emp === "object" && !Array.isArray(emp)) {
    return emp as U3130EmployeeRef;
  }
  return undefined;
}

/** Ordre aligné sur TsTimesheetStatus (backend) si sérialisation enum → entier. */
const U3130_TIMESHEET_STATUS_ENUM_INDEX: readonly U3130TimesheetStatus[] = [
  "Open",
  "WaitingForApproval",
  "Approve",
  "WaitingForGenerateTEC",
  "Close",
];

function u3130TimesheetStatusFromNumericEnum(v: unknown): U3130TimesheetStatus | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined;
  if (n < 0 || n >= U3130_TIMESHEET_STATUS_ENUM_INDEX.length) return undefined;
  return U3130_TIMESHEET_STATUS_ENUM_INDEX[n];
}

function pickStatusFromTimesheetRow(rec: Record<string, unknown>, fallback: U3130TimesheetStatus | undefined): U3130TimesheetStatus | undefined {
  const intLike = firstPresentIntegerLikeCandidate(rec.status, rec.Status, rec.timesheetStatus, rec.TimesheetStatus);
  const fromEnum = u3130TimesheetStatusFromNumericEnum(intLike);
  if (fromEnum) {
    return fromEnum;
  }
  const raw = firstPresentStringCandidate(rec.status, rec.Status, rec.timesheetStatus, rec.TimesheetStatus);
  if (raw && isU3130TimesheetStatusString(raw)) {
    return raw;
  }
  if (fallback != null && isU3130TimesheetStatusString(String(fallback))) {
    return fallback;
  }
  return undefined;
}

function pickEmployeeIdFromTimesheetRow(rec: Record<string, unknown>, fallback: string | undefined): string {
  const id = firstPresentStringCandidate(rec.employeeId, rec.EmployeeId) ?? fallback ?? "";
  return String(id).trim();
}

function omitKeys<T extends object, K extends keyof T>(obj: T, keys: readonly K[]): Omit<T, K> {
  const clone = { ...obj } as T;
  for (const key of keys) {
    delete clone[key];
  }
  return clone;
}

function normalizeExpenseLineFromApi(item: U3130ExpenseItem): U3130ExpenseItem {
  const sub = item.subproject;
  const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
  return {
    ...item,
    clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
    projectId: item.project?.id ?? item.projectId ?? "",
    subprojectId: subId || item.subprojectId?.trim() || "",
  };
}

function normalizeEquipmentLineFromApi(item: U3130EquipmentItem): U3130EquipmentItem {
  const sub = item.subproject;
  const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
  return {
    ...item,
    clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
    projectId: item.project?.id ?? item.projectId ?? "",
    subprojectId: subId || item.subprojectId?.trim() || "",
  };
}

/** Première valeur numérique finie ; sinon 0 — évite `null`/absent → champ décimal vide en lecture seule. */
function firstFiniteNumberOrZero(...vals: unknown[]): number {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Première valeur numérique finie ; sinon `undefined`. */
function firstDefinedFiniteNumber(...vals: unknown[]): number | undefined {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

type U3130EmployeeBankBalanceKey = "vacationBncHours" | "holidayBncHours" | "sickLeaveBncHours" | "specialBncHours";

const U3130_EMP_BANK_SNAKE: Record<U3130EmployeeBankBalanceKey, string> = {
  vacationBncHours: "vacation_bnc_hours",
  holidayBncHours: "holiday_bnc_hours",
  sickLeaveBncHours: "sick_leave_bnc_hours",
  specialBncHours: "special_bnc_hours",
};

const U3130_EMP_VAC_NEG_THRESHOLD_KEYS = [
  "vacationNegativeThresholdHours",
  "vacation_negative_threshold_hours",
  "VacationNegativeThresholdHours",
] as const;

const U3130_EMP_BANK_PASCAL: Record<U3130EmployeeBankBalanceKey, string> = {
  vacationBncHours: "VacationBncHours",
  holidayBncHours: "HolidayBncHours",
  sickLeaveBncHours: "SickLeaveBncHours",
  specialBncHours: "SpecialBncHours",
};

function mergeEmployeeBankBalancesFromTimesheetRow(emp: U3130EmployeeRef, rowRec: Record<string, unknown>): Partial<Pick<U3130EmployeeRef, U3130EmployeeBankBalanceKey>> {
  const empRec = emp as unknown as Record<string, unknown>;
  const out: Partial<Pick<U3130EmployeeRef, U3130EmployeeBankBalanceKey>> = {};
  (["vacationBncHours", "holidayBncHours", "sickLeaveBncHours", "specialBncHours"] as const).forEach((key) => {
    const sk = U3130_EMP_BANK_SNAKE[key];
    const pk = U3130_EMP_BANK_PASCAL[key];
    const fromEmployee = firstDefinedFiniteNumber(emp[key], empRec[sk], empRec[pk]);
    const v = fromEmployee !== undefined ? fromEmployee : firstDefinedFiniteNumber(rowRec[key], rowRec[sk]);
    if (v !== undefined) {
      (out as Record<string, number>)[key] = v;
    }
  });
  return out;
}

function resolveEmployeeVacationNegativeThreshold(emp: U3130EmployeeRef | undefined, rowRec: Record<string, unknown>): number | undefined {
  const empRec = (emp ?? {}) as Record<string, unknown>;
  return firstDefinedFiniteNumber(
    ...(U3130_EMP_VAC_NEG_THRESHOLD_KEYS.map((k) => empRec[k]) as unknown[]),
    ...(U3130_EMP_VAC_NEG_THRESHOLD_KEYS.map((k) => rowRec[k]) as unknown[]),
  );
}

/**
 * Feuille **Open** : soldes banque (priorité `employee`, racine timesheet, employé initLU).
 * Même logique que U3129 (`u3129ResolveOpenBankBalanceDisplaySource`).
 */
export function u3130ResolveOpenBankBalanceDisplaySource(
  data: U3130Timesheet | null | undefined,
  contextEmployee: U3130InitLUEmployee | null,
): Pick<U3130EmployeeRef, U3130EmployeeBankBalanceKey> {
  if (data == null) {
    return {
      vacationBncHours: contextEmployee?.vacationBncHours,
      holidayBncHours: contextEmployee?.holidayBncHours,
      sickLeaveBncHours: contextEmployee?.sickLeaveBncHours,
      specialBncHours: contextEmployee?.specialBncHours,
    };
  }
  const emp = data.employee;
  const empRec = (emp ?? {}) as Record<string, unknown>;
  const rowRec = data as unknown as Record<string, unknown>;
  return {
    vacationBncHours: firstDefinedFiniteNumber(
      emp?.vacationBncHours,
      empRec.vacation_bnc_hours,
      empRec.VacationBncHours,
      contextEmployee?.vacationBncHours,
      data.vacationBncHours,
      rowRec.vacation_bnc_hours,
    ),
    holidayBncHours: firstDefinedFiniteNumber(
      emp?.holidayBncHours,
      empRec.holiday_bnc_hours,
      empRec.HolidayBncHours,
      contextEmployee?.holidayBncHours,
      data.holidayBncHours,
      rowRec.holiday_bnc_hours,
    ),
    sickLeaveBncHours: firstDefinedFiniteNumber(
      emp?.sickLeaveBncHours,
      empRec.sick_leave_bnc_hours,
      empRec.SickLeaveBncHours,
      contextEmployee?.sickLeaveBncHours,
      data.sickLeaveBncHours,
      rowRec.sick_leave_bnc_hours,
    ),
    specialBncHours: firstDefinedFiniteNumber(
      emp?.specialBncHours,
      empRec.special_bnc_hours,
      empRec.SpecialBncHours,
      contextEmployee?.specialBncHours,
      data.specialBncHours,
      rowRec.special_bnc_hours,
    ),
  };
}

/**
 * Soldes banque pour l’onglet honoraires (lignes BNC + solde projeté vacances), tous statuts.
 * Hors « Ouvert » : priorité `employee` puis racine — aligné sur U3129 (`u3129ResolveHonorariumBankBalanceDisplaySource`).
 */
export function u3130ResolveHonorariumBankBalanceDisplaySource(
  data: U3130Timesheet | null | undefined,
  contextEmployee: U3130InitLUEmployee | null,
): Pick<U3130EmployeeRef, U3130EmployeeBankBalanceKey> {
  if (data == null || data.status === "Open") {
    return u3130ResolveOpenBankBalanceDisplaySource(data, contextEmployee);
  }
  const emp = data.employee;
  const rowRec = data as unknown as Record<string, unknown>;
  if (emp) {
    const m = mergeEmployeeBankBalancesFromTimesheetRow(emp, rowRec);
    return {
      vacationBncHours: firstDefinedFiniteNumber(
        m.vacationBncHours,
        rowRec.vacationBncHours,
        rowRec.vacation_bnc_hours,
      ),
      holidayBncHours: firstDefinedFiniteNumber(
        m.holidayBncHours,
        rowRec.holidayBncHours,
        rowRec.holiday_bnc_hours,
      ),
      sickLeaveBncHours: firstDefinedFiniteNumber(
        m.sickLeaveBncHours,
        rowRec.sickLeaveBncHours,
        rowRec.sick_leave_bnc_hours,
      ),
      specialBncHours: firstDefinedFiniteNumber(
        m.specialBncHours,
        rowRec.specialBncHours,
        rowRec.special_bnc_hours,
      ),
    };
  }
  return u3130ResolveOpenBankBalanceDisplaySource(data, contextEmployee);
}

/** Heures paye / banque : alias JSON possibles (camelCase, snake_case, PascalCase). */
function payrollHoursFromApiRow(row: Record<string, unknown>) {
  return {
    unpaidHours: firstFiniteNumberOrZero(row.unpaidHours, row.unpaid_hours, row.UnpaidHours),
    regularHoursToBank: firstFiniteNumberOrZero(row.regularHoursToBank, row.regular_hours_to_bank, row.RegularHoursToBank),
    halfTimeHoursToBank: firstFiniteNumberOrZero(row.halfTimeHoursToBank, row.half_time_hours_to_bank, row.HalfTimeHoursToBank),
    doubleTimeHoursToBank: firstFiniteNumberOrZero(row.doubleTimeHoursToBank, row.double_time_hours_to_bank, row.DoubleTimeHoursToBank),
    vacationBalanceBeforeTimesheet: firstDefinedFiniteNumber(
      row.vacationBalanceBeforeTimesheet,
      row.vacation_balance_before_timesheet,
      row.VacationBalanceBeforeTimesheet,
    ),
    vacationDebitHours: firstDefinedFiniteNumber(row.vacationDebitHours, row.vacation_debit_hours, row.VacationDebitHours),
    vacationBalanceAfterTimesheet: firstDefinedFiniteNumber(
      row.vacationBalanceAfterTimesheet,
      row.vacation_balance_after_timesheet,
      row.VacationBalanceAfterTimesheet,
    ),
    vacationThresholdHours: firstDefinedFiniteNumber(
      row.vacationThresholdHours,
      row.vacation_threshold_hours,
      row.VacationThresholdHours,
    ),
    vacationThresholdExceededHours: firstDefinedFiniteNumber(
      row.vacationThresholdExceededHours,
      row.vacation_threshold_exceeded_hours,
      row.VacationThresholdExceededHours,
    ),
    vacationBankWillBeNegative: (() => {
      const raw = row.vacationBankWillBeNegative ?? row.vacation_bank_will_be_negative ?? row.VacationBankWillBeNegative;
      if (raw === undefined || raw === null || raw === "") return undefined;
      if (typeof raw === "boolean") return raw;
      if (typeof raw === "number") return raw !== 0;
      if (typeof raw === "string") {
        const v = raw.trim().toLowerCase();
        if (v === "true" || v === "1") return true;
        if (v === "false" || v === "0") return false;
      }
      return undefined;
    })(),
  };
}

/** Normalise les honoraria reçues du GET (project/task) pour le formulaire (projectId/taskType). Complète toujours `bankHoursList`. */
export function normalizeTimesheetFromApi(row: U3130Timesheet): U3130Timesheet {
  const recTop = row as unknown as Record<string, unknown>;
  const statusAligned = pickStatusFromTimesheetRow(recTop, row.status);
  const employeeAligned = pickEmployeeObjectFromTimesheetRow(recTop);
  const employeeIdAligned = pickEmployeeIdFromTimesheetRow(recTop, row.employeeId);
  const rowAligned: U3130Timesheet = {
    ...row,
    ...(employeeIdAligned !== "" ? { employeeId: employeeIdAligned } : {}),
    ...(statusAligned !== undefined ? { status: statusAligned } : {}),
    ...(employeeAligned !== undefined ? { employee: employeeAligned } : {}),
  };

  const bankHoursList = ensureBankHoursList(rowAligned.bankHoursList);
  const rowLegacy = rowAligned as U3130Timesheet & { hourlyBase?: unknown };
  const rowWithoutRootHb = omitKeys(rowLegacy, ["hourlyBase"]);
  const emp = rowWithoutRootHb.employee;
  const hbEmployeeRaw = emp ? pickHourlyBaseRawFromEmployee(emp) : undefined;
  const rootHbRaw = pickHourlyBaseRawFromRowRoot(rowLegacy);
  const hourlyBaseMerged =
    hbEmployeeRaw !== undefined ? normalizeHourlyBaseFromApi(hbEmployeeRaw) : normalizeHourlyBaseFromApi(rootHbRaw);
  const rowRec = rowWithoutRootHb as unknown as Record<string, unknown>;
  const remunerationTypeMerged = emp ? pickRemunerationTypeFromEmployee(emp) : undefined;

  const shell: U3130EmployeeRef = { id: pickEmployeeIdFromTimesheetRow(rowRec, rowWithoutRootHb.employeeId) };
  const bankFromShellOnly = mergeEmployeeBankBalancesFromTimesheetRow(shell, rowRec);

  let employee: U3130EmployeeRef | undefined;
  if (emp) {
    const vacationNegativeThresholdHours = resolveEmployeeVacationNegativeThreshold(emp, rowRec);
    employee = {
      ...emp,
      hourlyBase: hourlyBaseMerged,
      ...(remunerationTypeMerged !== undefined ? { remunerationType: remunerationTypeMerged } : {}),
      ...mergeEmployeeBankBalancesFromTimesheetRow(emp, rowRec),
      ...(vacationNegativeThresholdHours !== undefined ? { vacationNegativeThresholdHours } : {}),
    };
  } else if (rootHbRaw !== undefined || hourlyBaseMerged !== null || Object.keys(bankFromShellOnly).length > 0) {
    const vacationNegativeThresholdHours = resolveEmployeeVacationNegativeThreshold(undefined, rowRec);
    employee = {
      ...shell,
      hourlyBase: hourlyBaseMerged,
      ...bankFromShellOnly,
      ...(vacationNegativeThresholdHours !== undefined ? { vacationNegativeThresholdHours } : {}),
    };
  } else {
    employee = undefined;
  }

  const base: U3130Timesheet = { ...rowWithoutRootHb, bankHoursList, employee };

  const honoraria = !base.honoraria?.length
    ? base.honoraria
    : base.honoraria.map((item) => {
        const sub = item.subproject;
        const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
        return {
          ...item,
          clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
          projectId: item.project?.id ?? item.projectId ?? "",
          subprojectId: subId || item.subprojectId?.trim() || "",
          taskType: item.task?.id ?? item.taskType ?? "",
        };
      });

  const expenses = (base.expenses ?? []).map((item) => normalizeExpenseLineFromApi(item));
  const equipment = (base.equipment ?? []).map((item) => normalizeEquipmentLineFromApi(item));

  const payrollHours = payrollHoursFromApiRow(rowRec);
  const out = { ...base, ...payrollHours, honoraria, expenses, equipment };
  const idEmpty = !String(out.id ?? "").trim();
  return {
    ...out,
    ...(out.status === undefined && idEmpty ? { status: "Open" as U3130TimesheetStatus } : {}),
  } as U3130Timesheet;
}
